import React, { useEffect } from 'react';
import Head from 'next/head';
import { NextRouter, withRouter } from 'next/router';
import NextSeo from 'next-seo';
import FacilityContainer from '@routes/Facility';
import { Property } from 'shared-types/Property';
import { NextPage, NextPageContext } from 'next';
import { analytics } from '@features/analytics';
import { getSpaSettingsPrefetch } from '@common/features/spa';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import {
  getSpaFacilityPrefetch,
  useGetSpaFacility,
} from '@common/features/spa/hooks/useGetSpaFacility';
import Error from 'next/error';

type FacilityPageProps = {
  id: string;
  property: Property;
  language: string;
  url: string;
  socialFallbackImage: { url?: string };
  router: NextRouter;
};

const FacilityPage: NextPage<FacilityPageProps> = ({
  id,
  property,
  language,
  socialFallbackImage = { url: '' },
  url,
  router,
}) => {
  const { data: facility } = useGetSpaFacility(property.id, language, id, null);

  if (!facility) {
    return <Error statusCode={404} />;
  }

  const pageTitle = property.propertyTitle
    ? `${facility.name} | ${property.propertyTitle}`
    : facility.name;
  const { bookingStep } = router.query;

  useEffect(() => {
    analytics.track({
      event: 'view_item',
      params: {
        currency: facility.price.currencyCode,
        value: facility.price.amount,
        items: [
          { item_category: 'spa-facilities', item_id: `${facility.id}`, item_name: facility.name },
        ],
      },
    });
  }, []);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            description: facility.summaryDescription,
            locale: language,
            url,
            images: [
              {
                url:
                  facility.imageGallery && facility.imageGallery.length
                    ? facility.imageGallery[0].url
                    : socialFallbackImage && socialFallbackImage.url,
              },
            ],
          },
        }}
      />
      <FacilityContainer
        id={router.query.id}
        key={id}
        property={property}
        language={language}
        bookingStep={bookingStep}
      />
    </>
  );
};

type FacilityInitialProps = {
  property: Property;
  language: string;
  url: string;
  store: any;
};

FacilityPage.getInitialProps = async ({
  property,
  language,
  query,
  url,
}: NextPageContext & FacilityInitialProps): Promise<FacilityPageProps> => {
  const client = new QueryClient();

  await Promise.all([
    getSpaSettingsPrefetch(client, property.id),
    getSpaFacilityPrefetch(client, property.id, language, query.id as string, null),
  ]);

  return {
    id: query.id as string,
    property,
    language,
    url,
    socialFallbackImage: property.socialFallbackImage,
    dehydratedState: dehydrate(client),
  } as any;
};

export default withRouter(FacilityPage as any);
